import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import {
  UserCircle,
  Crown,
  ArrowLeft,
  EnvelopeSimple,
  Phone,
  Buildings,
  CalendarBlank,
  Clock,
  CheckCircle,
  XCircle,
  Pencil
} from '@phosphor-icons/react'
import { getUserById, deleteUser, setUserActive, UserDetails } from '../../services/users'

export default function UserDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showError, showWarning, showSuccess, confirm } = useDialog()
  const [user, setUser] = useState<UserDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  async function loadUser() {
    if (!id) return
    try {
      const userData = await getUserById(id)
      setUser(userData)
    } catch (error) {
      console.error('Error loading user:', error)
      showError('Erro', 'Erro ao carregar usuário')
      navigate('/master/users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUser()
  }, [id, navigate])

  async function handleToggleActive() {
    if (!user) return
    const next = !user.isActive
    const confirmed = await confirm({
      title: next ? 'Ativar Conta' : 'Desativar Conta',
      message: next
        ? 'Reativar o acesso deste usuário?'
        : 'Desativar a conta? O usuário será deslogado e não poderá acessar.',
    })
    if (!confirmed) return
    setActionLoading(true)
    try {
      await setUserActive(user.id, next)
      showSuccess('Sucesso', next ? 'Conta ativada.' : 'Conta desativada.')
      await loadUser()
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao atualizar status')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleDelete() {
    if (!user) return
    const confirmed = await confirm({
      title: 'Remover Usuário',
      message: `Remover ${user.name || user.email} permanentemente? Esta ação não pode ser desfeita.`,
    })
    if (!confirmed) return
    setActionLoading(true)
    try {
      await deleteUser(user.id)
      showSuccess('Removido', 'Usuário removido com sucesso.')
      navigate('/master/users')
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao remover usuário')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando usuário...</div>
        </div>
      </AppLayout>
    )
  }

  if (!user) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Usuário não encontrado</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/users')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <h1 className="text-2xl font-semibold text-gray-900">Detalhes do Usuário</h1>
            </div>
            <Button
              onClick={() => showWarning('Em Desenvolvimento', 'Funcionalidade de edição em desenvolvimento')}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Pencil size={18} />
              Editar Usuário
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    {user.avatarUrl ? (
                      <img src={user.avatarUrl} alt={user.name} className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <UserCircle size={56} className="text-emerald-600" weight="duotone" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900">{user.name || 'Sem nome'}</h2>
                      {user.isMaster && (
                        <Badge className="bg-purple-100 text-purple-700 flex items-center gap-1">
                          <Crown size={12} weight="fill" />
                          Master Admin
                        </Badge>
                      )}
                      {user.isActive ? (
                        <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                          <CheckCircle size={12} weight="fill" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 flex items-center gap-1">
                          <XCircle size={12} weight="fill" />
                          Inativo
                        </Badge>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-gray-600">
                        <EnvelopeSimple size={18} weight="duotone" />
                        <span>{user.email}</span>
                      </div>
                      {user.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={18} weight="duotone" />
                          <span>{user.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Buildings size={18} weight="duotone" />
                        <span>{user.companiesCount} empresa{user.companiesCount !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Companies */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Buildings size={20} weight="duotone" />
                  Empresas Associadas
                </h3>

                {user.companies.length === 0 ? (
                  <div className="text-center py-8">
                    <Buildings size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                    <p className="text-gray-500 text-sm">Usuário não está associado a nenhuma empresa</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {user.companies.map((company) => (
                      <div
                        key={company.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-900">{company.name}</h4>
                              <Badge className={
                                company.type === 'shipper'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-orange-100 text-orange-700'
                              }>
                                {company.type === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                              </Badge>
                              <Badge variant="outline">
                                {company.role}
                              </Badge>
                            </div>
                            {company.trading_name && (
                              <p className="text-sm text-gray-600 mb-1">
                                Nome Fantasia: {company.trading_name}
                              </p>
                            )}
                            {company.document && (
                              <p className="text-sm text-gray-500">
                                {company.document}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/master/companies/${company.id}`)}
                          >
                            Ver Empresa
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Metadata */}
          <div className="space-y-6">
            {/* Account Status */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">Status da Conta</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status</span>
                    {user.isActive ? (
                      <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700">Inativo</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Tipo</span>
                    {user.isMaster ? (
                      <Badge className="bg-purple-100 text-purple-700">Master</Badge>
                    ) : (
                      <Badge variant="outline">Usuário</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Empresas</span>
                    <Badge variant="outline">{user.companiesCount}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dates */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">Informações de Data</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <CalendarBlank size={16} weight="duotone" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Criado em</span>
                    </div>
                    <p className="text-sm text-gray-900 pl-6">
                      {new Date(user.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Clock size={16} weight="duotone" />
                      <span className="text-xs font-medium text-gray-500 uppercase">Última atualização</span>
                    </div>
                    <p className="text-sm text-gray-900 pl-6">
                      {new Date(user.updatedAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 uppercase">Ações</h3>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => showWarning('Em Desenvolvimento', 'Funcionalidade em desenvolvimento')}
                  >
                    Resetar Senha
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    disabled={actionLoading}
                    onClick={handleToggleActive}
                  >
                    {user.isActive ? 'Desativar Conta' : 'Ativar Conta'}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={actionLoading}
                    onClick={handleDelete}
                  >
                    Remover Usuário
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </AppLayout>
  )
}
